import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

export const handleApiError = (error: unknown, context: string = ''): ApiError => {
  console.error(`[API Error] ${context}`, error);
  
  if (error instanceof AxiosError) {
    // Handle Axios errors
    const { response, request, message } = error;
    
    if (response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      return {
        message: response.data?.message || 'An error occurred',
        status: response.status,
        code: response.data?.code,
        details: response.data?.errors || response.data,
      };
    } else if (request) {
      // The request was made but no response was received
      return {
        message: 'No response received from server',
        status: 0,
        code: 'NO_RESPONSE',
      };
    }
    
    // Something happened in setting up the request that triggered an Error
    return {
      message: message || 'Error setting up request',
      code: 'REQUEST_ERROR',
    };
  } else if (error instanceof Error) {
    // Handle standard JavaScript errors
    return {
      message: error.message,
      code: 'ERROR',
    };
  }
  
  // Handle any other type of error
  return {
    message: 'An unknown error occurred',
    code: 'UNKNOWN_ERROR',
  };
};

export const useApiErrorHandler = () => {
  const { t } = useTranslation();
  
  const getErrorMessage = (error: unknown, defaultMessage: string = ''): string => {
    const apiError = handleApiError(error);
    
    // Map common error codes to user-friendly messages
    const errorCode = apiError.code !== undefined && apiError.code !== null 
      ? String(apiError.code)
      : 'UNKNOWN_ERROR';
    
    switch (errorCode) {
      case 'NETWORK_ERROR':
        return t('errors.networkError');
      case 'UNAUTHORIZED':
      case '401':
        return t('errors.unauthorized');
      case 'FORBIDDEN':
      case '403':
        return t('errors.forbidden');
      case 'NOT_FOUND':
      case '404':
        return t('errors.notFound');
      case 'VALIDATION_ERROR':
      case '422':
        return t('errors.validationError');
      case 'SERVER_ERROR':
      case '500':
        return t('errors.serverError');
      default:
        return apiError.message || defaultMessage || t('errors.unexpectedError');
    }
  };
  
  return {
    handleApiError: (error: unknown, context: string = '') => {
      return handleApiError(error, context);
    },
    getErrorMessage,
  };
};
